import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { type CourseDigital } from '@chancenwerk/db/courses/CourseDigital.js';
import { StudentType } from '@chancenwerk/db/enums/enums.js';
import type { LegalGuardian, Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import ImageLogo from '../../images/chancenwerk_logo_plaingreen.png';

const NO_DATA = html`<cr-text ${css({ opacity: '0.4' })}>Keine Angabe</cr-text>`;

/**
 * @customElement chancen-component-student-data-part-one
 */
@customElement('chancen-component-student-data-part-one')
export class ChancenComponentStudentDataPartOne extends ChemistryLitElement {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) student: Maybe<Student> = null;
  @property({ attribute: false }) schoolName: Maybe<string> = null;
  @property({ type: Boolean, attribute: 'hide-logos' }) hideLogos: boolean = false;

  override render() {
    const columns = this._size.mobile ? '1' : '2';
    const valueCssText = this._size.mobile ? {} : {};
    const valueCssHeading = this._size.mobile ? { fontWeight: 'bold' } : {};

    return html` <cr-flex space="lg">
      ${this.hideLogos
        ? nothing
        : html`<cr-image
            .value=${ImageLogo}
            is-url
            mode="width"
            ${css({ width: '33%', alignSelf: 'flex-end' })}
          ></cr-image>`}

      <!-- Persönliche Angaben -->
      <cr-card>
        <cr-flex space="sm">
          <cr-h4> Persönliche Angaben </cr-h4>
          <cr-grid columns="${columns}" space="sm">
            <cr-text ${css(valueCssHeading)}>Vorname</cr-text>
            <cr-text ${css(valueCssText)}>${this.student?.vorname ?? NO_DATA}</cr-text>

            <cr-text ${css(valueCssHeading)}>Nachname</cr-text>
            <cr-text ${css(valueCssText)}>${this.student?.nachname ?? NO_DATA}</cr-text>

            <cr-text ${css(valueCssHeading)}>Geschlecht</cr-text>
            <cr-text ${css(valueCssText)}>${this.student?.geschlecht ?? NO_DATA}</cr-text>

            ${this.student?.geburtstag
              ? html`
                  <cr-text ${css(valueCssHeading)}>Geburtstag</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student?.geburtstag?.toFormat('dd.MM.yyyy') ?? NO_DATA}</cr-text>
                `
              : nothing}
            ${this.student?.spokenLanguage
              ? html`
                  <cr-text ${css(valueCssHeading)}>gesprochene Sprache zu Hause</cr-text>
                  <cr-text ${css(valueCssText)}
                    >${this.student?.spokenLanguage ? this.student.spokenLanguage ?? 'Keine gewählt' : NO_DATA}</cr-text
                  >
                `
              : nothing}

            <cr-text ${css(valueCssHeading)}>Adresse</cr-text>
            <cr-text ${css(valueCssText)}>
              ${this.student?.adresse?.strasse}, ${this.student?.adresse?.zipCity?.zip}
              ${this.student?.adresse?.zipCity?.city}
            </cr-text>

            ${this.student?.email
              ? html` <cr-text ${css(valueCssHeading)}>E-Mail</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student?.email ?? NO_DATA}</cr-text>`
              : nothing}
            ${this.student?.handynummer
              ? html` <cr-text ${css(valueCssHeading)}>Handynummer</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student?.handynummer ?? NO_DATA}</cr-text>`
              : nothing}
          </cr-grid>
        </cr-flex>
      </cr-card>

      <!-- Schulische Angaben -->
      <cr-card>
        <cr-flex space="sm">
          <cr-h4> Schulische Angaben </cr-h4>
          <cr-grid columns="${columns}" space="sm">
            <cr-text ${css(valueCssHeading)}>Schule</cr-text>
            <cr-text ${css(valueCssText)}>
              ${this.schoolName ? this.schoolName : this.student?.kpfs?.at(0)?.school?.name}
            </cr-text>

            <cr-text ${css(valueCssHeading)}>Jahrgangsstufe</cr-text>
            <cr-text ${css(valueCssText)}>${this.student?.schoolInformation.jahrgangsstufe ?? NO_DATA}</cr-text>

            <cr-text ${css(valueCssHeading)}>Zug</cr-text>
            <cr-text ${css(valueCssText)}>${this.student?.schoolInformation.zug ?? NO_DATA}</cr-text>

            <cr-text ${css(valueCssHeading)}> Klassenlehrer:in </cr-text>
            <cr-text ${css(valueCssText)}> ${this.student?.schoolInformation.klassenlehrer ?? NO_DATA} </cr-text>
          </cr-grid>
        </cr-flex>
      </cr-card>

      ${this._legalGuardian(
        'Erste erziehungsberechtigte Person',
        this.student?.legalGuardian1,
        columns,
        valueCssText,
        valueCssHeading,
      )}
      ${this._legalGuardian(
        'Zweite erziehungsberechtigte Person',
        this.student?.legalGuardian2,
        columns,
        valueCssText,
        valueCssHeading,
      )}
    </cr-flex>`;
  }

  private _legalGuardian(
    title: string,
    guardian: Maybe<LegalGuardian>,
    columns: string,
    valueCssText: any,
    valueCssHeading: any,
  ) {
    if (
      !guardian ||
      (!guardian.vorname &&
        !guardian.nachname &&
        !guardian.geschlecht &&
        !guardian.staatsangehoerigkeit &&
        !guardian.beruf &&
        !guardian.handynummer &&
        !guardian.festnetznummer &&
        !guardian.email &&
        !(guardian?.adresse?.strasse || guardian.adresse?.zipCity?.zip || guardian.adresse?.zipCity?.city))
    ) {
      return nothing;
    }

    return html` <cr-card>
      <cr-flex space="sm">
        <cr-h4> ${title}</cr-h4>
        <cr-grid columns="${columns}" space="sm">
          <cr-text ${css(valueCssHeading)}>Vorname</cr-text>
          <cr-text ${css(valueCssText)}>${guardian.vorname ?? NO_DATA}</cr-text>

          <cr-text ${css(valueCssHeading)}>Nachname</cr-text>
          <cr-text ${css(valueCssText)}>${guardian.nachname ?? NO_DATA}</cr-text>

          <cr-text ${css(valueCssHeading)}>Geschlecht</cr-text>
          <cr-text ${css(valueCssText)}>${guardian.geschlecht ?? NO_DATA}</cr-text>

          ${guardian.spokenLanguage
            ? html`
                <cr-text ${css(valueCssHeading)}>gesprochene Sprache zu Hause</cr-text>
                <cr-text ${css(valueCssText)}
                  >${guardian.spokenLanguage ? guardian.spokenLanguage ?? 'Keine gewält' : NO_DATA}</cr-text
                >
              `
            : nothing}

          <cr-text ${css(valueCssHeading)}>Beruf</cr-text>
          <cr-text ${css(valueCssText)}>${guardian.beruf ?? NO_DATA}</cr-text>

          <cr-text>Handynummer</cr-text>
          <cr-text ${css(valueCssText)}>${guardian.handynummer ?? NO_DATA}</cr-text>

          <cr-text ${css(valueCssHeading)}>Festnetznummer</cr-text>
          <cr-text ${css(valueCssText)}>${guardian.festnetznummer ?? NO_DATA}</cr-text>

          <cr-text ${css(valueCssHeading)}>E-Mail</cr-text>
          <cr-text ${css(valueCssText)}>${guardian.email ?? NO_DATA}</cr-text>

          ${guardian?.adresse?.strasse || guardian.adresse?.zipCity?.zip || guardian.adresse?.zipCity?.city
            ? html`
                <cr-text ${css(valueCssHeading)}>Adresse</cr-text>
                <cr-text ${css(valueCssText)}>
                  ${guardian?.adresse?.strasse}, ${guardian.adresse?.zipCity?.zip} ${guardian.adresse?.zipCity?.city}
                </cr-text>
              `
            : nothing}
        </cr-grid>
      </cr-flex>
    </cr-card>`;
  }
}

/**
 * @customElement chancen-component-student-data-part-two
 */
@customElement('chancen-component-student-data-part-two')
export class ChancenComponentStudentDataPartTwo extends ChemistryLitElement {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) student: Maybe<Student> = null;
  @property({ attribute: false }) digitalCoursesChoosen: CourseDigital[] = [];
  @property({ type: Boolean, attribute: 'hide-courses' }) hideCourses: boolean = false;
  @property({ type: Boolean, attribute: 'hide-logos' }) hideLogos: boolean = false;
  @property({ type: Boolean, attribute: 'hide-additional-info' }) hideAdditionalInfo: boolean = false;
  @property({ type: Boolean, attribute: 'show-sensitive-data' }) showSensitiveData: boolean = false;

  override render() {
    const columns = this._size.mobile ? '1' : '2';
    const valueCssText = this._size.mobile ? {} : {};
    const valueCssHeading = this._size.mobile ? { fontWeight: 'bold' } : {};

    return html` <cr-flex space="lg">
      ${this.hideLogos
        ? nothing
        : html`<cr-image
            .value=${ImageLogo}
            is-url
            mode="width"
            ${css({ width: '33%', alignSelf: 'flex-end' })}
          ></cr-image>`}

      <!-- Abweichende Informationen -->
      ${!this.hideAdditionalInfo
        ? html` <cr-card>
            <cr-flex space="sm">
              <cr-h4> Zusätzliche Informationen </cr-h4>
              <cr-grid columns="${columns}" space="sm">
                <cr-text ${css(valueCssHeading)}>Abweichender Name Briefkasten</cr-text>
                <cr-text ${css(valueCssText)}
                  >${this.student?.zustellbesonderheiten && this.student.zustellbesonderheiten.length > 0
                    ? this.student.zustellbesonderheiten
                    : NO_DATA}</cr-text
                >

                <cr-text ${css(valueCssHeading)}>Präferierte Kontaktmethode</cr-text>
                <cr-text ${css(valueCssText)}
                  >${(this.student?.legalGuardianContactMethods ?? []).length > 0
                    ? (this.student?.legalGuardianContactMethods ?? []).join(', ')
                    : NO_DATA}</cr-text
                >
              </cr-grid>
            </cr-flex>
          </cr-card>`
        : nothing}
      ${this.student?.schuelertyp === StudentType.OS && !this.hideCourses
        ? html`
            <cr-card>
              <cr-flex space="sm">
                <cr-h4> Intensivkurs vor Ort </cr-h4>

                <cr-grid columns="${columns}" space="sm">
                  <cr-text ${css(valueCssHeading)}>Teilnahmetage</cr-text>
                  ${(this.student.intensiveCoursesChoosen ?? []).length === 0
                    ? html`<cr-text ${css(valueCssText)}>Keine!</cr-text>`
                    : nothing}
                  <cr-text ${css(valueCssText)}>
                    ${(this.student.intensiveCoursesChoosen ?? []).map(c => c.info).join(', ')}
                  </cr-text>
                </cr-grid>

                <cr-h4> Digitaler Intensivkurs </cr-h4>

                <cr-grid columns="${columns}" space="sm">
                  <cr-text ${css(valueCssHeading)}>Teilnahmetage</cr-text>
                  ${(this.digitalCoursesChoosen ?? []).length === 0
                    ? html`<cr-text ${css(valueCssText)}>Keine!</cr-text>`
                    : nothing}
                  <cr-text ${css(valueCssText)}>
                    ${(this.digitalCoursesChoosen ?? []).map(c => c.weekday).join(', ')}
                  </cr-text>
                </cr-grid>

                <cr-h4> Intensivkurs Einzeltermine </cr-h4>

                <cr-grid columns="${columns}" space="sm">
                  <cr-text ${css(valueCssHeading)}>Kurse</cr-text>
                  ${(this.student.intensiveCoursesSingleChoosen ?? []).length === 0
                    ? html`<cr-text ${css(valueCssText)}>Keine!</cr-text>`
                    : nothing}
                  <cr-flex space="md" ${css(valueCssText)}>
                    ${(this.student.intensiveCoursesSingleChoosen ?? []).map(c => html`<cr-text>${c.info}</cr-text>`)}
                  </cr-flex>
                </cr-grid>

                ${this.student?.schoolInformation.jahrgangsstufe === 10
                  ? html`
                      <cr-h4> Abschlussprüfungen Vorbereitung </cr-h4>

                      <cr-grid columns="${columns}" space="sm">
                        <cr-text ${css(valueCssHeading)}>Fächer</cr-text>
                        ${(this.student.examPreperationChoosen ?? []).length === 0
                          ? html`<cr-text ${css(valueCssText)}>Keine!</cr-text>`
                          : nothing}
                        <cr-text ${css(valueCssText)}>
                          ${(this.student.examPreperationChoosen ?? []).join(', ')}
                        </cr-text>
                      </cr-grid>
                    `
                  : nothing}
              </cr-flex>
            </cr-card>
          `
        : nothing}
      ${this.student?.schuelertyp === StudentType.US
        ? html`
            <!-- Förderbedarf -->
            <cr-card>
              <cr-flex space="sm">
                <cr-h4> Förderbedarf </cr-h4>
                <cr-grid columns="${columns}" space="sm">
                  <cr-text ${css(valueCssHeading)}>Förderbedarf</cr-text>
                  <cr-text ${css(valueCssText)}
                    >${(this.student.funding.foerderbedarfLautEltern ?? []).length > 0
                      ? (this.student.funding.foerderbedarfLautEltern ?? []).join(', ')
                      : NO_DATA}</cr-text
                  >

                  <cr-text ${css(valueCssHeading)}>Sonstige Fächer</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student.buts[0]?.sonstigeFaecherTmp ?? NO_DATA}</cr-text>

                  <cr-text ${css(valueCssHeading)}>Teilleistungsschwächen</cr-text>
                  <cr-text ${css(valueCssText)}>
                    ${this._getMedicalString(this.student.funding.teilleistungsschwaechen ?? [])}
                  </cr-text>

                  <cr-text ${css(valueCssHeading)}>Sonstige Hinweise zum Kind</cr-text>
                  <cr-text ${css(valueCssText)}
                    >${this.student.funding.teilleistungsschwaechenSonst ?? NO_DATA}</cr-text
                  >

                  <cr-text ${css(valueCssHeading)}>Teilnahmetage</cr-text>
                  ${(this.student.lernfoerderungDuerchfuehrungsTagChoosen ?? []).length === 0
                    ? html`<cr-text ${css(valueCssText)}>Keine!</cr-text>`
                    : nothing}
                  <cr-text ${css(valueCssText)}
                    >${(this.student.lernfoerderungDuerchfuehrungsTagChoosen ?? [])
                      .map(c => c.wochentag)
                      .join(', ')}</cr-text
                  >
                </cr-grid>
              </cr-flex>
            </cr-card>

            <!-- Finanzierung -->
            <cr-card>
              <cr-flex space="sm">
                <cr-h4> Finanzierung </cr-h4>
                <cr-grid columns="${columns}" space="sm">
                  <cr-text ${css(valueCssHeading)}>BuT Antragsteller:in</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student.buts[0]?.antragsteller ?? NO_DATA}</cr-text>

                  <cr-text ${css(valueCssHeading)}>BuT Aktenzeichen</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student.buts[0]?.aktenzeichen ?? NO_DATA}</cr-text>

                  <cr-text ${css(valueCssHeading)}>Mitgliedsbeitrag</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student.bankInformation.mitgliedsbeitrag ?? NO_DATA}</cr-text>

                  <cr-text ${css(valueCssHeading)}>Ich möchte weniger als 20€ bezahlen</cr-text>
                  <cr-text ${css(valueCssText)}
                    >${this.student.bankInformation.membershipFeeLowerThan20 ? 'Ja' : 'Nein'}</cr-text
                  >

                  <cr-text ${css(valueCssHeading)}>Kontoinhaber:in</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student.bankInformation.kontoinhaber ?? NO_DATA}</cr-text>

                  <cr-text ${css(valueCssHeading)}>IBAN</cr-text>
                  <cr-text ${css(valueCssText)}
                    >${this._getIbanString(this.student.bankInformation.iban ?? '')}</cr-text
                  >

                  <cr-text ${css(valueCssHeading)}>BIC</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student.bankInformation.bic ?? NO_DATA}</cr-text>

                  <cr-text ${css(valueCssHeading)}>Kreditinstitut</cr-text>
                  <cr-text ${css(valueCssText)}>${this.student.bankInformation.kreditinstitut ?? NO_DATA}</cr-text>
                </cr-grid>
              </cr-flex>
            </cr-card>
          `
        : nothing}
    </cr-flex>`;
  }

  private _getIbanString(iban: string) {
    if (iban === '') return NO_DATA;
    if (this.showSensitiveData) return iban;

    if (!iban) return '';
    return '**** **** **** **' + iban.slice(-5);
  }

  private _getMedicalString(medicalData: string[]) {
    if (medicalData.length === 0) return NO_DATA;
    if (this.showSensitiveData) return medicalData.join(', ');

    return medicalData.map(d => d.slice(0, 1) + '*****').join(', ');
  }
}

/**
 * @customElement chancen-component-student-data
 */
@customElement('chancen-component-student-data')
export class ChancenComponentStudentData extends ChemistryLitElement {
  @property({ attribute: false }) student: Maybe<Student> = null;
  @property({ attribute: false }) digitalCoursesChoosen: CourseDigital[] = [];
  @property({ attribute: false }) schoolName: Maybe<string> = null;
  @property({ type: Boolean, attribute: 'hide-courses' }) hideCourses: boolean = false;
  @property({ type: Boolean, attribute: 'hide-logos' }) hideLogos: boolean = false;
  @property({ type: Boolean, attribute: 'show-sensitive-data' }) showSensitiveData: boolean = false;
  @property({ type: Boolean, attribute: 'hide-additional-info' }) hideAdditionalInfo: boolean = false;

  override render() {
    return html`<cr-flex space="lg" padding="sm">
      <chancen-component-student-data-part-one
        .student=${this.student}
        .hideLogos=${this.hideLogos}
        .schoolName=${this.schoolName}
      ></chancen-component-student-data-part-one>
      <chancen-component-student-data-part-two
        .student=${this.student}
        .hideCourses=${this.hideCourses}
        .hideLogos=${this.hideLogos}
        .hideAdditionalInfo=${this.hideAdditionalInfo}
        .showSensitiveData=${this.showSensitiveData}
        .digitalCoursesChoosen=${this.digitalCoursesChoosen}
      ></chancen-component-student-data-part-two>
    </cr-flex>`;
  }
}
